import { useCallback, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, FileText, CheckCircle2, AlertTriangle, RotateCcw, Send, Shield, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const PROFILES = [
  { value: "particular", label: "Particular" },
  { value: "pyme", label: "Pyme / Negocio" },
  { value: "comunidad", label: "Comunidad" },
];

export default function BillAnalyzer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", profile: "particular" });
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = (f) => {
    setError(null);
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) { setError("Formato no soportado. Usa JPG, PNG, WEBP o PDF."); return; }
    if (f.size > 15 * 1024 * 1024) { setError("Archivo demasiado grande (máx. 15MB)."); return; }
    setFile(f);
    if (f.type.startsWith("image/")) setPreview(URL.createObjectURL(f));
    else setPreview(null);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) { setError("Adjunta una foto o PDF de tu factura."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      fd.append("profile", form.profile);
      fd.append("file", file);
      await axios.post(`${API}/study-request`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "No hemos podido enviar tu solicitud. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", profile: "particular" });
    setFile(null); setPreview(null); setDone(false); setError(null);
  };

  return (
    <div
      data-testid="bill-analyzer"
      className="relative w-full max-w-xl bg-white rounded-3xl border-2 border-ink shadow-[8px_8px_0_0_#0F0F0F] overflow-hidden"
    >
      {/* Yellow header strip */}
      <div className="bg-sun px-6 py-3 flex items-center justify-between border-b-2 border-ink">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-ink flex items-center justify-center">
            <FileText className="w-3.5 h-3.5 text-sun" strokeWidth={2} />
          </div>
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-ink">
            Estudio gratuito · Sin compromiso
          </span>
        </div>
        <span className="text-[11px] font-bold text-ink inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> 24h
        </span>
      </div>

      <div className="p-6 md:p-8">
        <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-[1.05] text-ink">
          Déjanos tu factura.<br />
          <span className="italic"><span className="tag-line">Te enviamos tu ahorro.</span></span>
        </h3>
        <p className="text-sm text-ink/65 mt-3 leading-relaxed">
          Nuestro equipo analiza tu factura a mano. En menos de 24h recibes un informe con el ahorro real y listo para proceder al cambio.
        </p>

        <AnimatePresence mode="wait">
          {!done && (
            <motion.form
              key="form"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onSubmit={submit}
              className="mt-5 space-y-3"
              data-testid="study-form"
            >
              {/* Profile selector */}
              <div className="flex gap-2" role="tablist" data-testid="profile-selector">
                {PROFILES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    role="tab"
                    aria-selected={form.profile === p.value}
                    onClick={() => setForm({ ...form, profile: p.value })}
                    data-testid={`profile-${p.value}`}
                    className={`flex-1 h-10 rounded-full text-sm font-semibold border-2 transition-colors
                      ${form.profile === p.value
                        ? "bg-ink text-sun border-ink"
                        : "bg-white text-ink border-ink/20 hover:border-ink"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  required value={form.name} onChange={update("name")}
                  placeholder="Tu nombre"
                  data-testid="study-name-input"
                  className="amql-input"
                />
                <input
                  required type="email" value={form.email} onChange={update("email")}
                  placeholder="Email"
                  data-testid="study-email-input"
                  className="amql-input"
                />
              </div>
              <input
                value={form.phone} onChange={update("phone")}
                placeholder="Teléfono (opcional)"
                data-testid="study-phone-input"
                className="amql-input"
              />

              <label
                htmlFor="bill-input"
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                data-testid="bill-dropzone"
                className={`relative flex flex-col items-center justify-center cursor-pointer rounded-2xl border-2 border-dashed transition-all px-6 py-7 text-center
                  ${drag ? "border-ink bg-sun/20" : "border-ink/30 hover:border-ink hover:bg-sun/10 bg-paper"}`}
              >
                <div className="w-11 h-11 rounded-full bg-ink text-sun flex items-center justify-center">
                  <Upload className="w-5 h-5" strokeWidth={2} />
                </div>
                <p className="font-semibold text-ink mt-3 text-sm">
                  {file ? file.name : "Adjunta tu factura"}
                </p>
                <p className="text-xs text-ink/60 mt-1">
                  {file ? `${(file.size / 1024).toFixed(0)} KB · listo` : "JPG, PNG, WEBP o PDF · máx. 15MB"}
                </p>
                <input
                  id="bill-input" data-testid="bill-file-input" type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>

              {preview && (
                <div className="flex items-center gap-3 p-2.5 rounded-xl bg-paper border border-ink/10">
                  <img src={preview} alt="preview" className="w-10 h-10 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate text-ink">{file?.name}</p>
                    <p className="text-xs text-ink/60">100% confidencial</p>
                  </div>
                  <button type="button" onClick={() => { setFile(null); setPreview(null); }}
                    data-testid="bill-reset-button"
                    className="text-xs text-ink/60 hover:text-ink underline">Quitar</button>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span data-testid="bill-error">{error}</span>
                </div>
              )}

              <button
                type="submit" disabled={loading}
                data-testid="bill-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-ink text-sun font-bold hover:bg-sun hover:text-ink border-2 border-ink transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {loading ? "Enviando…" : "Quiero mi estudio gratuito"}
              </button>

              <p className="text-[11px] text-ink/55 text-center inline-flex items-center justify-center gap-1.5 w-full">
                <Shield className="w-3 h-3" /> Sin compromiso · borramos tu factura tras el estudio
              </p>
            </motion.form>
          )}

          {done && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-6 rounded-2xl bg-ink text-white p-6 text-center relative overflow-hidden"
              data-testid="study-success"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-sun/20 rounded-full blur-3xl" />
              <div className="relative">
                <CheckCircle2 className="w-10 h-10 text-sun mx-auto" strokeWidth={1.5} />
                <h4 className="font-serif text-2xl text-white mt-3">¡Recibido!</h4>
                <p className="text-white/70 text-sm mt-2 max-w-sm mx-auto">
                  En menos de <strong className="text-sun">24h</strong> recibirás en tu correo el estudio personalizado con tu ahorro real y los pasos para proceder al cambio.
                </p>
                <button
                  onClick={reset} data-testid="study-reset-button"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-sun hover:underline"
                >
                  <RotateCcw className="w-4 h-4" /> Enviar otra factura
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        .amql-input {
          width: 100%;
          background: #fff;
          border: 2px solid rgba(15,15,15,0.15);
          border-radius: 12px;
          padding: 11px 14px;
          color: #0F0F0F;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color .2s;
        }
        .amql-input::placeholder { color: rgba(15,15,15,0.40); }
        .amql-input:focus { border-color: #0F0F0F; }
      `}</style>
    </div>
  );
}
